import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import type {
  ChtLimits,
  CylinderCount,
  EngineConfig,
  EngineReadings,
  Units,
} from '../types/engine';
import {
  applyCylinderCht,
  applyCylinderEgt,
  applyMagRise,
  makeEmptyReadings,
  resetMagCheck,
  resetPeaks,
} from '../lib/readings';

type StoreState = {
  config: EngineConfig;
  readings: EngineReadings;
};

type Action =
  | { type: 'setCylinderCount'; count: CylinderCount }
  | { type: 'setChtLimits'; limits: ChtLimits }
  | { type: 'setUnits'; units: Units }
  | { type: 'setCylinderEgt'; id: number; egt: number | undefined }
  | { type: 'setCylinderCht'; id: number; cht: number | undefined }
  | { type: 'setMagRise'; id: number; side: 'left' | 'right'; rise: number | undefined }
  | { type: 'setPower'; power: number | undefined }
  | { type: 'setFuelFlow'; fuelFlow: number | undefined }
  | { type: 'setTas'; tas: number | undefined }
  | { type: 'setFuelQty'; fuelQty: number | undefined }
  | { type: 'clearReadings' }
  | { type: 'resetPeaks' }
  | { type: 'resetMagCheck' };

const DEFAULT_CHT_LIMITS: ChtLimits = { ok: 380, warn: 400, danger: 420 };

const DEFAULT_CONFIG: EngineConfig = {
  cylinderCount: 6,
  chtLimits: DEFAULT_CHT_LIMITS,
  units: 'imperial',
};

const initialState: StoreState = {
  config: DEFAULT_CONFIG,
  readings: makeEmptyReadings(DEFAULT_CONFIG.cylinderCount),
};

function reducer(state: StoreState, action: Action): StoreState {
  switch (action.type) {
    case 'setCylinderCount':
      return {
        config: { ...state.config, cylinderCount: action.count },
        readings: makeEmptyReadings(action.count),
      };
    case 'setChtLimits':
      return { ...state, config: { ...state.config, chtLimits: action.limits } };
    case 'setUnits':
      return { ...state, config: { ...state.config, units: action.units } };
    case 'setCylinderEgt':
      return { ...state, readings: applyCylinderEgt(state.readings, action.id, action.egt) };
    case 'setCylinderCht':
      return { ...state, readings: applyCylinderCht(state.readings, action.id, action.cht) };
    case 'setMagRise':
      return {
        ...state,
        readings: applyMagRise(state.readings, action.id, action.side, action.rise),
      };
    case 'setPower':
      return {
        ...state,
        readings: { ...state.readings, power: action.power, lastUpdated: Date.now() },
      };
    case 'setFuelFlow':
      return {
        ...state,
        readings: { ...state.readings, fuelFlow: action.fuelFlow, lastUpdated: Date.now() },
      };
    case 'setTas':
      return {
        ...state,
        readings: { ...state.readings, tas: action.tas, lastUpdated: Date.now() },
      };
    case 'setFuelQty':
      return {
        ...state,
        readings: { ...state.readings, fuelQty: action.fuelQty, lastUpdated: Date.now() },
      };
    case 'clearReadings':
      return { ...state, readings: makeEmptyReadings(state.config.cylinderCount) };
    case 'resetPeaks':
      return { ...state, readings: resetPeaks(state.readings) };
    case 'resetMagCheck':
      return { ...state, readings: resetMagCheck(state.readings) };
  }
}

type StoreValue = {
  config: EngineConfig;
  readings: EngineReadings;
  setCylinderCount: (count: CylinderCount) => void;
  setChtLimits: (limits: ChtLimits) => void;
  setUnits: (units: Units) => void;
  setCylinderEgt: (id: number, egt: number | undefined) => void;
  setCylinderCht: (id: number, cht: number | undefined) => void;
  setMagRise: (id: number, side: 'left' | 'right', rise: number | undefined) => void;
  setPower: (power: number | undefined) => void;
  setFuelFlow: (fuelFlow: number | undefined) => void;
  setTas: (tas: number | undefined) => void;
  setFuelQty: (fuelQty: number | undefined) => void;
  clearReadings: () => void;
  resetPeaks: () => void;
  resetMagCheck: () => void;
};

const EngineStoreContext = createContext<StoreValue | null>(null);

export function EngineStoreProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const value = useMemo<StoreValue>(
    () => ({
      config: state.config,
      readings: state.readings,
      setCylinderCount: (count) => dispatch({ type: 'setCylinderCount', count }),
      setChtLimits: (limits) => dispatch({ type: 'setChtLimits', limits }),
      setUnits: (units) => dispatch({ type: 'setUnits', units }),
      setCylinderEgt: (id, egt) => dispatch({ type: 'setCylinderEgt', id, egt }),
      setCylinderCht: (id, cht) => dispatch({ type: 'setCylinderCht', id, cht }),
      setMagRise: (id, side, rise) => dispatch({ type: 'setMagRise', id, side, rise }),
      setPower: (power) => dispatch({ type: 'setPower', power }),
      setFuelFlow: (fuelFlow) => dispatch({ type: 'setFuelFlow', fuelFlow }),
      setTas: (tas) => dispatch({ type: 'setTas', tas }),
      setFuelQty: (fuelQty) => dispatch({ type: 'setFuelQty', fuelQty }),
      clearReadings: () => dispatch({ type: 'clearReadings' }),
      resetPeaks: () => dispatch({ type: 'resetPeaks' }),
      resetMagCheck: () => dispatch({ type: 'resetMagCheck' }),
    }),
    [state],
  );

  return <EngineStoreContext.Provider value={value}>{children}</EngineStoreContext.Provider>;
}

export function useEngineStore(): StoreValue {
  const ctx = useContext(EngineStoreContext);
  if (!ctx) {
    throw new Error('useEngineStore must be used inside <EngineStoreProvider>');
  }
  return ctx;
}
